import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarBlank, ArrowsCounterClockwise, Plus, Warning, X } from '@phosphor-icons/react';
import { ProjectsGantt } from './ProjectsGantt';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VIEW_MODES = ['Day', 'Week', 'Month'];
const TODAY = new Date().toISOString().slice(0, 10);

const computeBucket = (p) => {
  if (p.archived) return 'archived';
  const status = (p.status || '').toLowerCase();
  if (status === 'planned' || status === 'previsionnel') return 'planned';
  if (status === 'termine' || status === 'done' || status === 'cloture') return 'done';
  // Auto: a project with start_date in the future is treated as planned
  if (p.start_date && p.start_date > TODAY && status !== 'en_cours') return 'planned';
  return 'current';
};

const BUCKET_LABEL = { current: 'En cours', planned: 'Previsionnel', done: 'Terminé', leave: 'Conge / Absence' };
const BUCKET_COLOR = { current: '#1565C0', planned: '#FB8C00', done: '#2E7D32', leave: '#9CA3AF' };

const LEAVE_TYPE_LABEL = { conge: 'Conges', maladie: 'Maladie', absence: 'Absence' };

export const ProjectsGanttView = ({ projects, onProjectChanged }) => {
  const [viewMode, setViewMode] = useState('Week');
  const [filter, setFilter] = useState('all'); // all | current | planned
  const [showLeaves, setShowLeaves] = useState(true);
  const [leaves, setLeaves] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [conflictsDismissed, setConflictsDismissed] = useState(false);
  const [conflictsExpanded, setConflictsExpanded] = useState(false);

  const fetchLeaves = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/leaves?status=approved`, { withCredentials: true });
      setLeaves(r.data || []);
    } catch { /* silent — leaves are optional overlay */ }
  }, []);

  const fetchConflicts = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/projects/leave-conflicts`, { withCredentials: true });
      setConflicts(r.data || []);
    } catch { /* silent — alert is optional */ }
  }, []);

  useEffect(() => { fetchLeaves(); fetchConflicts(); }, [fetchLeaves, fetchConflicts]);

  const tasks = useMemo(() => {
    const out = [];
    // 1) Projects
    (projects || []).forEach(p => {
      if (p.archived || !p.start_date) return;
      const b = computeBucket(p);
      if (filter === 'current' && b !== 'current') return;
      if (filter === 'planned' && b !== 'planned') return;
      out.push({
        id: p.id,
        name: p.name,
        start: p.start_date,
        end: p.end_date || p.start_date,
        progress: 0,
        custom_class: `bb-gantt-${b}`,
        project: p,
      });
    });
    // 2) Approved leaves overlay (always rendered when showLeaves)
    if (showLeaves) {
      (leaves || []).forEach(l => {
        if (!l.start_date) return;
        out.push({
          id: `leave-${l.id}`,
          name: `${l.user_name} — ${LEAVE_TYPE_LABEL[l.type] || 'Absence'}`,
          start: l.start_date,
          end: l.end_date || l.start_date,
          progress: 0,
          custom_class: 'bb-gantt-leave',
          project: { client_name: l.reason || '' },
        });
      });
    }
    return out;
  }, [projects, filter, leaves, showLeaves]);

  const counts = useMemo(() => {
    const out = { current: 0, planned: 0, done: 0, total_with_dates: 0, total_no_dates: 0, leaves: 0 };
    (projects || []).forEach(p => {
      if (p.archived) return;
      if (!p.start_date) { out.total_no_dates++; return; }
      const b = computeBucket(p);
      if (out[b] !== undefined) out[b]++;
      out.total_with_dates++;
    });
    (leaves || []).forEach(l => { if (l.start_date) out.leaves++; });
    return out;
  }, [projects, leaves]);

  const handleDateChange = async (task, newStart, newEnd) => {
    // Leaves are read-only — revert any drag
    if (String(task.id).startsWith('leave-')) {
      toast.info('Les conges/absences ne sont pas modifiables ici. Allez dans Conges & Absences.');
      fetchLeaves(); // forces a reload to revert the visual
      return;
    }
    try {
      await axios.put(`${API_URL}/api/projects/${task.id}`, { start_date: newStart, end_date: newEnd }, { withCredentials: true });
      toast.success(`${task.name} : ${newStart} -> ${newEnd}`);
      fetchConflicts();
      if (onProjectChanged) onProjectChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur mise a jour');
    }
  };

  const handleClick = (task) => {
    if (String(task.id).startsWith('leave-')) return; // no detail page for a leave
    const p = (projects || []).find(x => x.id === String(task.id));
    if (p && window.confirm(`Ouvrir la fiche du chantier "${p.name}" ?`)) {
      window.location.href = `/projects/${p.id}`;
    }
  };

  return (
    <div data-testid="projects-gantt-view">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        {/* Filter pills */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { k: 'all', label: `Tous (${counts.current + counts.planned})` },
            { k: 'current', label: `En cours (${counts.current})`, color: BUCKET_COLOR.current },
            { k: 'planned', label: `Previsionnel (${counts.planned})`, color: BUCKET_COLOR.planned },
          ].map(opt => (
            <button key={opt.k} onClick={() => setFilter(opt.k)} data-testid={`gantt-filter-${opt.k}`}
              style={{
                padding: '6px 14px', borderRadius: 999, fontSize: '0.82rem', fontWeight: 600,
                border: '1px solid', cursor: 'pointer',
                borderColor: filter === opt.k ? (opt.color || '#002FA7') : '#D1D5DB',
                backgroundColor: filter === opt.k ? (opt.color || '#002FA7') : 'white',
                color: filter === opt.k ? 'white' : '#1F2937',
              }}>{opt.label}</button>
          ))}
        </div>
        {/* View mode + leaves toggle */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: '#5E5F66', cursor: 'pointer', marginRight: 8 }}>
            <input type="checkbox" checked={showLeaves} onChange={(e) => setShowLeaves(e.target.checked)} data-testid="gantt-toggle-leaves" />
            Afficher conges {counts.leaves > 0 && <span style={{ color: '#9CA3AF' }}>({counts.leaves})</span>}
          </label>
          {VIEW_MODES.map(m => (
            <button key={m} onClick={() => setViewMode(m)} data-testid={`gantt-view-${m.toLowerCase()}`}
              style={{
                padding: '6px 12px', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600,
                border: '1px solid #D1D5DB', cursor: 'pointer',
                backgroundColor: viewMode === m ? '#002FA7' : 'white',
                color: viewMode === m ? 'white' : '#1F2937',
              }}>{m === 'Day' ? 'Jour' : m === 'Week' ? 'Semaine' : 'Mois'}</button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 8, fontSize: '0.78rem', color: '#5E5F66', flexWrap: 'wrap' }}>
        {Object.entries(BUCKET_COLOR).map(([k, c]) => {
          if (k === 'leave' && !showLeaves) return null;
          return (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: c, display: 'inline-block' }} />
              {BUCKET_LABEL[k]}
            </span>
          );
        })}
        <span style={{ marginLeft: 'auto', fontStyle: 'italic' }}>
          Glissez les barres pour deplacer. Tirez les bords pour redimensionner.
        </span>
      </div>

      {/* Leave conflicts alert */}
      {conflicts.length > 0 && !conflictsDismissed && (
        <div
          data-testid="gantt-conflicts-alert"
          style={{
            marginBottom: 12,
            padding: '10px 14px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FCA5A5',
            borderRadius: 8,
            color: '#7F1D1D',
            fontSize: '0.85rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Warning size={20} weight="fill" color="#DC2626" />
              <strong>
                {conflicts.length} conflit{conflicts.length > 1 ? 's' : ''} detecte{conflicts.length > 1 ? 's' : ''} :
              </strong>
              <span>un salarie assigne est en conge pendant la periode du chantier.</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                data-testid="gantt-conflicts-toggle"
                onClick={() => setConflictsExpanded(v => !v)}
                style={{
                  padding: '4px 12px', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600,
                  border: '1px solid #DC2626', cursor: 'pointer',
                  backgroundColor: conflictsExpanded ? '#DC2626' : 'white',
                  color: conflictsExpanded ? 'white' : '#DC2626',
                }}
              >
                {conflictsExpanded ? 'Masquer' : 'Voir le detail'}
              </button>
              <button
                type="button"
                data-testid="gantt-conflicts-dismiss"
                onClick={() => setConflictsDismissed(true)}
                title="Ignorer pour cette session"
                style={{
                  padding: '4px 8px', borderRadius: 6, border: '1px solid transparent',
                  background: 'transparent', cursor: 'pointer', color: '#7F1D1D',
                  display: 'inline-flex', alignItems: 'center',
                }}
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          </div>
          {conflictsExpanded && (
            <div data-testid="gantt-conflicts-list" style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {conflicts.map((c, i) => {
                const leaveLabel = LEAVE_TYPE_LABEL[c.leave_type] || 'Absence';
                return (
                  <div
                    key={`${c.project_id}-${c.leave_id}-${i}`}
                    data-testid={`gantt-conflict-${i}`}
                    style={{
                      padding: '8px 10px',
                      background: 'white',
                      border: '1px solid #FECACA',
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ padding: '2px 8px', borderRadius: 4, background: '#E3F2FD', color: '#1565C0', fontWeight: 700, fontSize: '0.78rem' }}>
                      {c.project_name}
                    </span>
                    <span style={{ fontWeight: 600 }}>{c.user_name}</span>
                    <span style={{ padding: '1px 7px', borderRadius: 999, background: '#F3F4F6', color: '#374151', fontSize: '0.72rem', fontWeight: 600 }}>
                      {leaveLabel}
                    </span>
                    <span style={{ fontSize: '0.78rem', color: '#5E5F66' }}>
                      du {c.overlap_start} au {c.overlap_end}
                    </span>
                    {c.project_id && (
                      <a
                        href={`/projects/${c.project_id}`}
                        style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#1565C0', fontWeight: 600, textDecoration: 'none' }}
                      >
                        Ouvrir le chantier →
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tasks.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: '#9CA3AF' }} data-testid="gantt-empty">
          <CalendarBlank size={42} weight="thin" />
          <p style={{ marginTop: 8 }}>Aucun chantier avec des dates a afficher.</p>
          {counts.total_no_dates > 0 && (
            <p style={{ fontSize: '0.78rem', marginTop: 4 }}>{counts.total_no_dates} chantier(s) sans dates de debut/fin definies.</p>
          )}
        </div>
      ) : (
        <div className="card" style={{ padding: 12, overflow: 'hidden' }}>
          <ProjectsGantt
            tasks={tasks}
            viewMode={viewMode}
            onDateChange={handleDateChange}
            onClick={handleClick}
          />
        </div>
      )}

      {counts.total_no_dates > 0 && tasks.length > 0 && (
        <div style={{ marginTop: 10, padding: '8px 12px', backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, fontSize: '0.78rem', color: '#92400E' }}>
          <strong>{counts.total_no_dates} chantier(s)</strong> sans dates de debut/fin ne sont pas affiches. Renseignez-les depuis la fiche chantier pour les voir ici.
        </div>
      )}
    </div>
  );
};
